/**
 * UK Retirement Planning Benchmarks
 * Sources: PLSA Retirement Living Standards (2024), Fidelity salary multiples
 */

// PLSA Retirement Living Standards (2024 annual figures)
export const PLSA_TIERS = {
  minimum: { single: 14_400, couple: 22_400 },
  moderate: { single: 31_300, couple: 43_100 },
  comfortable: { single: 43_100, couple: 59_000 },
} as const;

export type PLSATier = keyof typeof PLSA_TIERS;

// Full new State Pension (2026/27: £241.30/week × 52). Update both together each April.
export const STATE_PENSION_TAX_YEAR = '2026/27';
export const STATE_PENSION_ANNUAL = 12_547.60;

// Fidelity salary multiples by age
const FIDELITY_MILESTONES: ReadonlyArray<{ age: number; multiple: number }> = [
  { age: 30, multiple: 1 },
  { age: 35, multiple: 2 },
  { age: 40, multiple: 3 },
  { age: 45, multiple: 4 },
  { age: 50, multiple: 5 },
  { age: 55, multiple: 7 },
  { age: 60, multiple: 7 },
  { age: 67, multiple: 8 },
];

/**
 * Get the Fidelity salary multiple for a given age via linear interpolation.
 * Below 30 returns 0; above 67 returns 8.
 */
export function getFidelityMultiple(age: number): number {
  if (age < 30) return 0;
  if (age >= 67) return 8;

  for (let i = 0; i < FIDELITY_MILESTONES.length - 1; i++) {
    const curr = FIDELITY_MILESTONES[i];
    const next = FIDELITY_MILESTONES[i + 1];
    if (age >= curr.age && age < next.age) {
      const fraction = (age - curr.age) / (next.age - curr.age);
      return curr.multiple + fraction * (next.multiple - curr.multiple);
    }
  }

  return 8;
}

// Safe Withdrawal Rate (commonly used for decumulation planning)
export const SAFE_WITHDRAWAL_RATE = 0.04;

// Income replacement target (common rule of thumb)
export const INCOME_REPLACEMENT_TARGET = 0.667;

/**
 * State Pension age based on birth year.
 * Born before 6 April 1960 → 66 (already reached or past SP age).
 * Born 6 April 1960 to 5 April 1977 → 67.
 * Born on or after 6 April 1977 → 68 (Pensions Act 2014).
 * Uses birth year as a simplified proxy (ignores month of birth).
 */
export function getStatePensionAge(birthYear: number): number {
  if (birthYear >= 1977) return 68;
  if (birthYear >= 1960) return 67;
  return 66;
}

// State Pension deferral: +1% per 9 weeks ≈ 5.8% per year
export const STATE_PENSION_DEFERRAL_RATE = 0.058;
